//deps
import { useContext, useEffect, useMemo, useState } from "react";

//internal-deps
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { GetWalletDataResponsePayload } from "@swiggy-private/native-bridge";
import { getCloudinaryUrl, useMountedRef } from "@swiggy-private/core-ui";
import { WALLET_CODES } from "@swiggy-private/payment-types";

//context
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

//helpers
import { getWalletDetailsFromPaymentResponse } from "@payment-client/helpers/WalletHelper";

//constant
import { WALLET_PAYMENT_CODES } from "@payment-client/constants/Wallet";

interface WalletDetails {
    iconUrl: string;
    paymentCode: string;
}

export interface GetLinkedWalletDetails {
    walletDetails?: WalletDetails;
}

export const useGetWalletDetails = (walletCode: WALLET_CODES, isPaymentModeRoute: boolean): GetLinkedWalletDetails => {
    const [isLoading, setIsLoading] = useState(isPaymentModeRoute);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const isMountedRef = useMountedRef();

    const [walletDetails, setWalletDetails] = useState<WalletDetails | undefined>(
        !isPaymentModeRoute ? getWalletDetailsFromPaymentResponse(paymentResponse, walletCode) : undefined,
    );

    useEffect(() => {
        if (!isLoading || !isMountedRef.current) {
            return;
        }

        getBridgeSdk()
            .getWalletDetails({ walletCode })
            .then((walletDetails: GetWalletDataResponsePayload | undefined) => {
                if (!isMountedRef.current) {
                    return;
                }

                setWalletDetails({
                    paymentCode: WALLET_PAYMENT_CODES[walletCode],
                    iconUrl: walletDetails ? getCloudinaryUrl(walletDetails.iconUrl) : "",
                });
                setIsLoading(false);
            })
            .catch(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setIsLoading(false);
                setWalletDetails({
                    paymentCode: WALLET_PAYMENT_CODES[walletCode],
                    iconUrl: "",
                });
            });
    }, [isLoading, walletCode, isMountedRef]);

    const useGetWalletDetailsReturnObj = useMemo(() => {
        return {
            walletDetails,
        };
    }, [walletDetails]);

    return useGetWalletDetailsReturnObj;
};
